import React from "react";
import { Grid, Box, Typography, alpha, useTheme, LinearProgress } from "@mui/material";
import { keyframes } from "@mui/system";
import { formatBytes } from "../../../utils/common";
import {
  Assignment as AssignmentIcon,
  Speed as SpeedIcon,
  DirectionsRun as DirectionsRunIcon,
  CurrencyExchange as CurrencyExchangeIcon,
  CloudDownload as DownloadIcon,
  CloudUpload as UploadIcon,
  DataUsage as DataUsageIcon,
  WifiTethering as WifiTetheringIcon,
  Analytics as AnalyticsIcon,
  AllInclusive as AllInclusiveIcon,
} from "@mui/icons-material";
import certificateRibbonBadge from "../../../assets/illustrations/certificate_ribbon_badge.png";

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const float = keyframes`
  0% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-10px) rotate(2deg); }
  100% { transform: translateY(0px) rotate(0deg); }
`;

const SectionTitle = ({ title, icon: Icon, color = "primary" }) => {
  const theme = useTheme();
  const mainColor = theme.palette[color]?.main || theme.palette.primary.main;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        mb: 3,
        p: 2,
        borderRadius: "16px",
        background: `linear-gradient(135deg, ${alpha(mainColor, 0.12)} 0%, ${alpha(
          mainColor,
          0.04,
        )} 100%)`,
        border: `1px solid ${alpha(mainColor, 0.15)}`,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 38,
          height: 38,
          borderRadius: "12px",
          bgcolor: mainColor,
          color: "white",
          boxShadow: `0 8px 16px ${alpha(mainColor, 0.25)}`,
        }}
      >
        <Icon sx={{ fontSize: "1.3rem" }} />
      </Box>
      <Typography
        variant="subtitle1"
        sx={{
          fontWeight: 900,
          letterSpacing: "-0.01em",
          color: "text.primary",
          fontSize: "0.95rem",
        }}
      >
        {title}
      </Typography>
    </Box>
  );
};

const PropertyTile = ({ label, value, icon: Icon, highlight, color = "primary" }) => {
  const theme = useTheme();
  const mainColor = theme.palette[color]?.main || theme.palette.primary.main;

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: "18px",
        bgcolor: alpha(theme.palette.background.paper, 0.4),
        border: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        display: "flex",
        flexDirection: "column",
        gap: 0.5,
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          width: "4px",
          height: "0%",
          bgcolor: mainColor,
          transition: "height 0.3s ease",
          borderRadius: "0 4px 4px 0",
        },
        "&:hover": {
          bgcolor: alpha(theme.palette.background.paper, 1),
          transform: "translateY(-4px)",
          boxShadow: "0 12px 24px rgba(0,0,0,0.04)",
          borderColor: alpha(mainColor, 0.2),
          "&::before": {
            height: "100%",
          },
          "& .tile-icon": {
            color: mainColor,
            transform: "scale(1.1)",
          },
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            fontSize: "0.65rem",
          }}
        >
          {label}
        </Typography>
        {Icon && (
          <Icon
            className="tile-icon"
            sx={{
              fontSize: "1rem",
              color: alpha(theme.palette.text.secondary, 0.3),
              transition: "all 0.3s ease",
            }}
          />
        )}
      </Box>
      <Typography
        sx={{
          color: highlight ? mainColor : "text.primary",
          fontWeight: 900,
          fontSize: "0.95rem",
          letterSpacing: "-0.01em",
          lineHeight: 1.2,
        }}
      >
        {value || "---"}
      </Typography>
    </Box>
  );
};

const UsageTile = ({ label, value, percent, icon: Icon, color = "primary" }) => {
  const theme = useTheme();
  const mainColor = theme.palette[color]?.main || theme.palette.primary.main;

  return (
    <Box
      sx={{
        p: 2.5,
        borderRadius: "20px",
        bgcolor: alpha(theme.palette.background.paper, 0.4),
        border: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Icon sx={{ fontSize: "1.1rem", color: mainColor }} />
          <Typography
            variant="caption"
            sx={{ fontWeight: 800, color: "text.secondary", textTransform: "uppercase" }}
          >
            {label}
          </Typography>
        </Box>
        <Typography variant="caption" sx={{ fontWeight: 900, color: mainColor }}>
          {percent}%
        </Typography>
      </Box>
      <Box>
        <LinearProgress
          variant="determinate"
          value={percent}
          sx={{
            height: 8,
            borderRadius: 4,
            bgcolor: alpha(mainColor, 0.1),
            "& .MuiLinearProgress-bar": {
              borderRadius: 4,
              background: `linear-gradient(90deg, ${mainColor} 0%, ${alpha(mainColor, 0.5)} 100%)`,
            },
          }}
        />
      </Box>
      <Typography
        variant="caption"
        sx={{ fontWeight: 800, color: "text.primary", textAlign: "right" }}
      >
        {value}
      </Typography>
    </Box>
  );
};

const PlanInformation = ({ user }) => {
  const theme = useTheme();

  const isOnline =
    user?.connectionStatus?.toLowerCase() === "online" ||
    user?.isOnline === "true" ||
    user?.isOnline === true ||
    user?.isOnline?.toString()?.toLowerCase() === "online";

  return (
    <Box sx={{ mb: 4 }}>
      <Grid container spacing={3}>
        {/* PLAN DETAILS */}
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              p: 3,
              height: "100%",
              borderRadius: "28px",
              bgcolor: (theme) => (theme.palette.mode === "dark" ? "background.paper" : "white"),
              border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
              display: "flex",
              flexDirection: "column",
              gap: 2,
              position: "relative",
              overflow: "hidden",
              animation: `${fadeInUp} 0.6s ease-out 0.5s forwards`,
              opacity: 0,
            }}
          >
            <Box
              component="img"
              src={certificateRibbonBadge}
              sx={{
                position: "absolute",
                bottom: 10,
                right: 10,
                width: "80px",
                opacity: 0.1,
                mixBlendMode: "multiply",
                pointerEvents: "none",
                animation: `${float} 5s ease-in-out infinite alternate`,
              }}
            />
            <SectionTitle title="Plan Subscription" icon={WifiTetheringIcon} color="primary" />
            <Grid container spacing={2} sx={{ position: "relative", zIndex: 1 }}>
              <Grid item xs={12}>
                <PropertyTile
                  label="Assigned Plan"
                  value={user.planName || "NA"}
                  icon={AssignmentIcon}
                  highlight
                />
              </Grid>
              <Grid item xs={isOnline ? 6 : 12}>
                <PropertyTile
                  label="Mode"
                  value={user.quotaLimit == "0" ? "Unlimited" : "FUP"}
                  icon={SpeedIcon}
                  color={user.quotaLimit == "0" ? "success" : "warning"}
                />
              </Grid>
              {isOnline && (
                <Grid item xs={6}>
                  <PropertyTile
                    label="Running Plan"
                    value={user.runningPlan}
                    icon={DirectionsRunIcon}
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <PropertyTile
                  label="Rate Name"
                  value={user.rateFullName}
                  icon={CurrencyExchangeIcon}
                />
              </Grid>
            </Grid>
          </Box>
        </Grid>

        {/* DATA CONSUMPTION */}
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              p: 3,
              height: "100%",
              borderRadius: "28px",
              bgcolor: (theme) => (theme.palette.mode === "dark" ? "background.paper" : "white"),
              border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
              display: "flex",
              flexDirection: "column",
              gap: 2,
              position: "relative",
              overflow: "hidden",
              animation: `${fadeInUp} 0.6s ease-out 0.6s forwards`,
              opacity: 0,
            }}
          >
            <AllInclusiveIcon
              sx={{
                position: "absolute",
                bottom: -20,
                right: -20,
                fontSize: "140px",
                opacity: 0.03,
                color: theme.palette.info.main,
                animation: `${float} 7s ease-in-out infinite`,
              }}
            />
            <SectionTitle title="Data Consumption" icon={AnalyticsIcon} color="info" />
            <Grid container spacing={2} sx={{ position: "relative", zIndex: 1 }}>
              {user.quotaLimit != "0" ? (
                <>
                  <Grid item xs={12}>
                    <UsageTile
                      label="Data Consumption Progress"
                      value={`${formatBytes(user.usedQuotaLimit)} / ${formatBytes(user.quotaLimit)}`}
                      percent={Math.round((user.usedQuotaLimit / user.quotaLimit) * 100) || 0}
                      icon={DataUsageIcon}
                      color="info"
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <PropertyTile
                      label="Total Data"
                      value={formatBytes(user.quotaLimit)}
                      icon={AnalyticsIcon}
                      color="info"
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <PropertyTile
                      label="Used Data"
                      value={formatBytes(user.usedQuotaLimit)}
                      icon={DataUsageIcon}
                      color="warning"
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <PropertyTile
                      label="Balance Data"
                      value={formatBytes(Math.max(0, user.quotaLimit - user.usedQuotaLimit))}
                      icon={WifiTetheringIcon}
                      color="success"
                    />
                  </Grid>
                </>
              ) : (
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      py: 2,
                    }}
                  >
                    <Box
                      sx={{
                        position: "relative",
                        display: "inline-flex",
                        mb: 2,
                      }}
                    >
                      <Box
                        sx={{
                          width: 120,
                          height: 120,
                          borderRadius: "50%",
                          border: `8px solid ${alpha(theme.palette.info.main, 0.1)}`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <AllInclusiveIcon sx={{ fontSize: 48, color: "info.main" }} />
                      </Box>
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 900, color: "text.primary" }}>
                      Unlimited
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 800, color: "text.secondary" }}>
                      Data Plan
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PlanInformation;
